import { useEffect, useState } from "react";
import { Quote, MapPin, CalendarDays } from "lucide-react";
import Reveal from "@/components/Reveal";
import { fetchTestimonials } from "@/lib/testimonials";
import { RESORTS } from "@/data/resorts";

const resortName = (slug) => RESORTS.find((r) => r.slug === slug)?.name || slug;

/**
 * Prova social REAL: só renderiza quando existem depoimentos aprovados
 * cadastrados pelo painel administrativo. Enquanto não houver nenhum,
 * a seção fica completamente oculta no site público.
 */
export default function Proofs() {
    const [items, setItems] = useState([]);

    useEffect(() => {
        fetchTestimonials().then(setItems);
    }, []);

    if (!items.length) return null;

    return (
        <section id="depoimentos" className="bg-white py-24 sm:py-32" data-testid="proofs-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <Reveal className="text-center">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-hover">Prova Social</p>
                    <h2 className="mx-auto mt-3 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-forest-deep sm:text-4xl lg:text-5xl">
                        Quem viajou pelo Destino Resorts
                    </h2>
                    <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-stone-600">
                        Avaliações reais de clientes que solicitaram cotação e viajaram pelo portal.
                    </p>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {items.map((t, i) => (
                        <Reveal key={t.id} delay={i * 0.12}>
                            <article
                                className="flex h-full flex-col rounded-3xl border border-[#E5DEC9] bg-sand p-7 shadow-sm transition-shadow duration-300 hover:shadow-xl hover:shadow-forest-deep/10"
                                data-testid={`proof-card-${t.id}`}
                            >
                                <Quote className="h-7 w-7 text-gold" />
                                <p className="mt-4 flex-1 text-sm leading-relaxed text-stone-700">“{t.text}”</p>
                                <footer className="mt-6 flex items-center gap-3 border-t border-[#E5DEC9] pt-5">
                                    {t.photo_url ? (
                                        <img
                                            src={t.photo_url}
                                            alt={`Foto de ${t.client_name}`}
                                            loading="lazy"
                                            className="h-11 w-11 rounded-full object-cover"
                                        />
                                    ) : (
                                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest-emerald font-serif text-base font-semibold text-white">
                                            {t.client_name.trim().charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                    <div>
                                        <p className="text-sm font-semibold text-forest-deep">{t.client_name}</p>
                                        <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-stone-500">
                                            {t.resort && (
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin className="h-3 w-3 text-gold-hover" />
                                                    {resortName(t.resort)}
                                                </span>
                                            )}
                                            {t.travel_date && (
                                                <span className="inline-flex items-center gap-1">
                                                    <CalendarDays className="h-3 w-3 text-gold-hover" />
                                                    Viagem em {t.travel_date}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </footer>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
